/**
 * Canonical public marketplace URLs under /grenada/* (trailing slash in sitemap + prerender).
 * Legacy /shop, /product/*, etc. are handled at the CDN (amplify.yml 301 / prerender) — do not link them in-app.
 */

export const MARKETPLACE = {
  hub: '/grenada/',
  shop: '/grenada/shop/',
  vendors: '/grenada/vendors/',
  classifieds: '/grenada/classifieds/',
  realEstate: '/grenada/real-estate/',
  findAPro: '/grenada/find-a-pro/',
  deals: '/grenada/deals/',
  /** Post flow (not island-prefixed — parish is chosen in the form; published listings use classifiedPath). */
  classifiedsPost: '/grenada/classifieds/post/',
} as const;

/** Compare paths ignoring optional trailing slash. */
export function pathsMatch(a: string, b: string): boolean {
  const norm = (p: string) => p.replace(/\/+$/, '') || '/';
  return norm(a) === norm(b);
}

/** Public indexable path with trailing slash (matches NormalizeTrailingSlash + sitemap). */
export function withPublicTrailingSlash(pathname: string): string {
  const base = pathname.replace(/\/+$/, '') || '/';
  return base === '/' ? '/' : `${base}/`;
}

/** True when pathname is the hub or a deeper path under it. */
export function pathUnder(pathname: string, hub: string): boolean {
  const base = hub.replace(/\/+$/, '') || '/';
  const path = pathname.replace(/\/+$/, '') || '/';
  return path === base || path.startsWith(`${base}/`);
}

/** Desktop nav strip keys (feature-flagged in Navbar). */
export type MarketplaceNavStripKey = 'deals' | 'vendors' | 'property' | 'classifieds' | 'advertise';

/** Canonical href for marketplace nav strip — never use legacy `/shop`, `/property`, etc. */
export function marketplaceNavHref(key: MarketplaceNavStripKey): string {
  switch (key) {
    case 'deals':
      return MARKETPLACE.deals;
    case 'vendors':
      return MARKETPLACE.vendors;
    case 'property':
      return MARKETPLACE.realEstate;
    case 'classifieds':
      return MARKETPLACE.classifieds;
    case 'advertise':
      return '/advertise/';
  }
}

/** Grenada vendors directory with hire/book mode. */
export function vendorsDirectoryUrl(params?: { mode?: 'hire' | 'book'; sector?: string }): string {
  const search = new URLSearchParams();
  if (params?.mode) search.set('mode', params.mode);
  if (params?.sector) search.set('sector', params.sector);
  const qs = search.toString();
  return qs ? `${MARKETPLACE.vendors}?${qs}` : MARKETPLACE.vendors;
}
